import type { IBatchStore } from './batch-store-types';
import moment from 'moment';
import shortid from 'shortid';
import { toNumber } from '../../utils/numbers';
import logger from '../../utils/logger';
import { BatchCycleStatus } from '../../constants/BatchCycleStatus';
import { BatchTicketStatus } from '../../constants/BatchTicketStatus';
import { OrderOrigin } from '../../constants/OrderOrigin';
import { SwitchMode } from '../../components/DeviceAsDom/Switch';
import { truckSwitch } from './device-switches/truck-switch';
import { hornSwitch } from './device-switches/horn-switch';
import { checkNewVersionAvailable } from './check-new-version-available';
import { cleanupBatchPrecisionLoss } from './precision-loss-handler';
import { timerKey } from '../../utils/timer-manager/timer-manager';
import type { IBatchData } from '../../containers/Batch/IBatchData';
import type { MixDesignItem } from '../../../types/batch/MixDesignItem';
import { isFeatureEnabled } from '../../utils/feature-flags';
import {
  IMixer,
  ISwitch,
} from '../../../types/device/DeviceModels';

const FEATURE_ACKNOWLEDGE_FINISH_BATCH = isFeatureEnabled('acknowledge_finish_batch');

export const batchCompletionMixin = {

  /**
   * Check if you can batch next drop
   */
  nextStep(this: IBatchStore): void {
    logger.log('NEXT STEP');

    this.timers.clear(timerKey.checkDataInScaleQueue());
    this.timers.set(timerKey.checkDataInScaleQueue(), () => this.checkDataInScaleQueue(), 2000, 'Check data in scale queue');

    this.timers.clear(timerKey.nextStep());
    this.timers.set(timerKey.nextStep(), () => this.startBatch(), 3000, 'Next step');

    if (this.isDischarge) {
      this.discharge();
    }
  },

  /**
   * Print ticket on batch end
   */
  printTicketOnBatchEnd(this: IBatchStore, data: IBatchData, ticket: any): void {
    const { printStore, ticketStore, settingsStore } = this.rootStore;
    const { configuration } = settingsStore;
    // @ts-ignore
    const { printOnlyWeightsTemplateId, printWithWeightsTemplateId, printWithoutWeightsTemplateId, numberOfTicketCopies, disableLoadPrint } = configuration;

    if (disableLoadPrint) {
      return;
    }

    ticket = ticketStore.calculateTicketData(ticket, false);

    if (data.printWithWeights) {
      ticketStore.getTemplate(printWithWeightsTemplateId)
        .finally(() => {
          printStore.printTemplate({ ...ticket, isDuplicate: false }, numberOfTicketCopies);
        });
    }

    if (data.printWithoutWeightsAtEnd) {
      ticketStore.getTemplate(printWithoutWeightsTemplateId)
        .finally(() => {
          printStore.printTemplate({ ...ticket, isDuplicate: false }, numberOfTicketCopies);
        });
    }

    if (data.printOnlyWeights) {
      ticketStore.getTemplate(printOnlyWeightsTemplateId)
        .finally(() => {
          printStore.printTemplate({ ...ticket, isDuplicate: false, onlyWeights: true }, numberOfTicketCopies);
        });
    }
  },

  /**
   * Save result for each drop
   */
  saveResultForDrop(this: IBatchStore, runningData: IBatchData, data: MixDesignItem): void {
    if (!runningData?.currentDrop || !data) {
      return;
    }

    if (!runningData.batchDrops[runningData.currentDrop]) {
      runningData.batchDrops[runningData.currentDrop] = [];
    }

    let index = runningData.batchDrops[runningData.currentDrop].findIndex((v: any) => v.constituentItemId === data.constituentItemId);
    if (index === -1) {
      runningData.batchDrops[runningData.currentDrop].push(data);
    } else {
      runningData.batchDrops[runningData.currentDrop][index] = data;
    }

    this.running.forEach(v => {
      if (v.currentDrop > runningData.currentDrop && v.uid === runningData.uid) {
        v.batchDrops = { ...runningData.batchDrops };
      }
    });
    this.queue.forEach(v => {
      if (v.currentDrop > runningData.currentDrop && v.uid === runningData.uid) {
        v.batchDrops = { ...runningData.batchDrops };
      }
    });

    logger.log('save drops', data, runningData.batchDrops);
  },

  saveDropTimes(this: IBatchStore, runningData: IBatchData, data: { dropNumber: number, loadStartTime: any, loadEndTime: any }): void {
    let index = runningData.dropTimes.findIndex(v => v.dropNumber === runningData.currentDrop);

    if (index > -1 && data.loadEndTime) {
      runningData.dropTimes[index].loadEndTime = data.loadEndTime;
    } else if (index > -1 && data.loadStartTime) {
      runningData.dropTimes[index].loadStartTime = data.loadStartTime;
    } else if (index === -1 && data.loadStartTime) {
      runningData.dropTimes.push(data);
    }

    this.running.forEach(v => {
      index = runningData.dropTimes.findIndex(v => v.dropNumber === runningData.currentDrop);
      if (v.uid === runningData.uid) {
        v.dropTimes = [ ...runningData.dropTimes ];
      }
    });
    this.queue.forEach(v => {
      if (v.uid === runningData.uid) {
        v.dropTimes = [ ...runningData.dropTimes ];
      }
    });

    logger.log('dropTimes', runningData.dropTimes, data);
  },

  /**
   * Send data to API on batch end
   */
  sendBatchFinish(this: IBatchStore, data: IBatchData): void {
    if (!data?.mixDesign || data?.resultsSending) {
      this.showSuccess({ key: 'Load completed!', props: { loadId: data ? data.tId || data.uid : '' } });

      if (!data.isWashout) {
        truckSwitch(this.rootStore, false);
        hornSwitch(this.rootStore);
        this.context.onBatchCycle(BatchCycleStatus.FINISH, this.inputs, this.outputs, this.channels);

        if (data.currentDrop === data.allDrops) {
          this.context.onBatchCycle(BatchCycleStatus.FINISH_LOAD, this.inputs, this.outputs, this.channels);
        }
      }

      return;
    }

    const { ticketStore, scheduleStore, connectionStore, settingsStore } = this.rootStore;
    const { configuration } = settingsStore;
    data.loadEndTime = moment().tz(this.timezone).add((data.loadStartTimeDiff || 0) * -1).format();
    this.saveDropTimes(data, {
      dropNumber: data.currentDrop,
      loadEndTime: data.loadEndTime,
    });
    let ticket = ticketStore.calculateTicketData({ ...data });
    data.results = ticket.results;

    const dataIndex = this.running.findIndex((v: any) => v.uid === data.uid && v.currentDrop === data.currentDrop);
    const nextData = this.running.length > dataIndex + 1 ? this.running[dataIndex + 1] : this.queue.length ? this.queue[0] : null;

    if (nextData?.uid === data.uid) {
      nextData.results = ticket.results;
      logger.log(`${data.uid} drop ${data.currentDrop} of ${data.allDrops} - next drop`, data, nextData.results);
      return;
    }

    logger.log('batch drops', data.batchDrops);
    data.drops = ticketStore.convertDrops(data.batchDrops);
    logger.log('drops', data.drops);

    this.showSuccess({ key: 'Load completed!', props: { loadId: data.tId || data.uid } });
    truckSwitch(this.rootStore, false);
    hornSwitch(this.rootStore);
    this.context.onBatchCycle(BatchCycleStatus.FINISH, this.inputs, this.outputs, this.channels);

    if (data.currentDrop === data.allDrops) {
      this.context.onBatchCycle(BatchCycleStatus.FINISH_LOAD, this.inputs, this.outputs, this.channels);
    }

    if (this.isDemo && !this.canDemoBatch) {
      logger.log('Demo: finish batch...');
      return;
    }

    const order = data.order?.products ? data.order : data;
    const mix = Array.isArray(order.products) ? order.products.find((v: any) => v.isMix) || {} : {};
    const orderDeliveredMixQuantity = toNumber(order.orderDeliveredMixQuantity || 0) + toNumber(mix.quantity || 0);
    delete ticket.mixDesign;

    if (data.batchStartedOffline) {
      if (order.origin === OrderOrigin.Local) {
        ticket.id = `${ticket.id}-${shortid.generate()}`;
      }

      ticket = {
        ...ticket,
        drops: data.drops || [],
        batchStartedOffline: true,
        origin: order.origin,
        isOffline: true,
        dischargeSequenceId: data.dischargeSequenceId,
      };
      const ticketObj = order.origin === OrderOrigin.External
        ? { ticket }
        : { tickets: [ticket] };

      const offlineData = {
        ...order,
        products: data.products || [],
        date: data.loadStartTime,
        loadEndTime: data.loadEndTime,
        orderDeliveredMixQuantity,
        batchTicketStatus: orderDeliveredMixQuantity >= order.orderMixQuantity ? BatchTicketStatus.Batched : BatchTicketStatus.None,
        ...ticketObj,
      };

      if (offlineData.dispatchOrderId) {
        ticketStore.updateOfflineTickets(offlineData);
      } else {
        scheduleStore.updateOfflineOrder(offlineData);
      }
    }

    if (!connectionStore.isOnline) {
      if (!data.batchStartedOffline) {
        ticketStore.updateTicketOnBatchEndOffline({
          ...ticket,
          id: data.id,
          batchFinishOffline: true,
          origin: order.origin,
          idDemo: this.isDemo,
        });
      }

      if (!configuration?.printAsSoonAsMaterialsAreWeighed) {
        this.printTicketOnBatchEnd(data, ticket);
      }

    } else if (!data.batchStartedOffline) {

      logger.log(`${data.uid} drop ${data.currentDrop} of ${data.allDrops} - batch finish`, data, 'results sending?', data.resultsSending);
      this.saveLog(`Ticket ${data.id} batch finish, results sending? ${data.resultsSending}`);

      if (!data.resultsSending) {
        data.resultsSending = true;
        ticketStore.updateTicketOnBatchEnd(data)
          .then((resp: any) => {
            data.resultsSent = true;
            logger.log('Batch ticket end', resp);
            this.saveLog(`Batch weights sent, finish load ${data.id}`);
            const ticket = ticketStore.calculateTicketData(resp);
            this.printTicketOnBatchEnd(data, ticket);
          })
          .catch((e: any) => {
            this.showError({ key: 'Couldn\'t update ticket on batch end', props: { error: e.message } });

            if (!configuration?.printAsSoonAsMaterialsAreWeighed) {
              this.printTicketOnBatchEnd(data, ticket);
            }
          });
      }

    } else if (data.batchStartedOffline) {
      this.scheduleStore.syncOfflineOrders();
    }
  },

  /**
   * Send feed logs
   */
  sendFeedLogs(this: IBatchStore, batchData: IBatchData): void {
    const { jogStore } = this.rootStore;
    let logs: any[] = [];

    if (!batchData.scales || batchData.isWashout || batchData.material || this.isTestMode || batchData.isDemo) {
      return;
    }

    Object.keys(batchData.scales).forEach((scaleId) => {
      batchData.scales[scaleId].forEach((data: any) => {
        if (data.feedLogs.length && !data.isFeedInterrupted) {
          logs = [...logs, ...data.feedLogs];
        }
      });
    });

    logger.table(logs);

    if (!logs.length) {
      logger.log('No feed logs');
    }

    jogStore.saveFeedLogs(batchData.id, logs);
  },

  resetSwitchLatchOn(this: IBatchStore): void {
    const { devicesStore } = this.rootStore;
    const batchData = this.running.length ? this.running[0] : null;
    const queue = this.queue.length ? this.queue[0] : null;

    devicesStore.switches
      .filter((v: ISwitch) => v.mode === SwitchMode.LatchOn)
      .forEach((v: ISwitch) => {
        const ds = this.getDeviceStatus(v.id);
        if (
          (!batchData && !queue)
          ||
          (batchData && ds.uid !== batchData.uid)
          ||
          (!batchData && queue && ds.uid !== queue.uid)
        ) {
          this.deviceStatus.set(v.id, {
            ...this.getDeviceStatusDefault(v),
          });
        }
      });
  },

  resetMixers(this: IBatchStore): void {
    const { devicesStore } = this.rootStore;

    devicesStore.mixers.forEach((mixer: IMixer) => {
      const ds = this.getDeviceStatus(mixer.id);
      if (
        !this.running.length
        ||
        !this.running.some(v => v.uid === ds.uid)
      ) {
        this.deviceStatus.set(mixer.id, {
          ...this.getDeviceStatusDefault(mixer),
        });
      }
    });
  },

  /**
   * Finish batch
   */
  finishBatch(this: IBatchStore, runningData: IBatchData, isFinishBatchConfirmed: boolean = false): void {
    const { configuration } = this.settingsStore;

    logger.log('FINISH BATCH', runningData, this.running.length, this.isActiveDrop(runningData), isFinishBatchConfirmed);

    if (!runningData || !this.running.length || !this.isActiveDrop(runningData)) {
      return;
    }

    if (
      FEATURE_ACKNOWLEDGE_FINISH_BATCH
      && configuration.acknowledgeCompleteLoads
      && !isFinishBatchConfirmed
      && runningData.mixDesign
      && !runningData.isAborted
      && runningData.currentDrop === runningData.allDrops
      && !this.isAbort
    ) {
      this.isFinishBatchPanelVisible = true;
      return;
    } else {
      this.isFinishBatchPanelVisible = false;
    }

    if (
      (this.running.length === 1 && !this.queue.length)
      ||
      (this.running.length > 1 && this.running[1] && this.running[0].uid !== this.running[1].uid
        && !this.running[0].mixerId && !this.running[1].mixerId)
      ||
      (this.running.length === 1 && this.queue[0] && this.running[0].uid !== this.queue[0].uid)
      ||
      this.isHoldDischarge
    ) {
      this.isDischarge = false;
      this.isHoldDischarge = false;
    }

    this.sendBatchFinish(runningData);
    this.sendFeedLogs(runningData);

    if (this.running.length === 1) {
      this.lastLoad = { ...this.running[0] };
    }

    const d = this.running.slice();
    this.running = d.filter((v: any) => v._id !== runningData._id);

    // Clean up precision loss data if this was the last drop of the batch
    if (runningData.currentDrop === runningData.allDrops) {
      // Check if there are no more drops for this batch UID
      const hasMoreDrops = this.running.some((r: any) => r.uid === runningData.uid) ||
        (this.queue.length && this.queue.some((q: any) => q.uid === runningData.uid));
      if (!hasMoreDrops) {
        cleanupBatchPrecisionLoss(runningData.uid);
        logger.log(`Cleaned up precision loss data for batch ${runningData.uid}`);
      }
    }

    if (!this.running.length) {
      const { remoteDisplayStore } = this.rootStore;
      remoteDisplayStore.clearStatuses();

      this.clearTimers();
      this.isTestMode = false;

      if (!this.queue.length) {
        this.devicesStore.filterPorts();
      }

      checkNewVersionAvailable(this);
    }

    if (!this.queue.length && !this.running.length && this.isDemo) {
      this.timers.set(timerKey.finishBatch(), () => {
        if (!this.queue.length && !this.running.length && this.isDemo) {
          this.pacSimulator.reset();
        }
      }, 2000);
    } else if (this.queue.length && (this.queue[0].uid === runningData.uid || configuration.enableTurboTicket)) {
      this.nextStep();
    } else if (this.running.length && this.running[0].mixerId && this.isDischarge) {
      this.discharge();
    }

    this.resetSwitchLatchOn();
    this.resetMixers();
  },

};
